/**
 * Data layer for review comments — threads for one page (the editor's
 * Comment mode) or every page (the Change requests inbox).
 *
 * Plain request/response, no optimistic tricks: a comment is a deliberate
 * submit, so each mutation waits for the server (which assigns the pin
 * number) and swaps the returned thread into state. A 30s poll + refetch on
 * window focus keeps two reviewers roughly in sync; the polling is quiet
 * (errors there never surface — the next tick retries).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE } from './config';
import { authHeaders } from './auth';
import type { ReviewAnchor, ReviewKind, ReviewStatus, ReviewThread, ReviewViewport } from './review';

const POLL_MS = 30_000;
const BASE = `${API_BASE}/api/admin/review`;

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.body !== undefined),
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string } & T;
  if (!res.ok) throw new Error(data.error ?? `Request failed (${res.status})`);
  return data;
}

export interface NewThreadInput {
  page: string;
  viewport: ReviewViewport;
  viewportWidth?: number;
  anchor: ReviewAnchor;
  body: string;
  kind: ReviewKind;
}

export function useReview(page?: string) {
  const [threads, setThreads] = useState<ReviewThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pageRef = useRef(page);
  pageRef.current = page;
  // Stale-response guards. A load that was already in flight when a mutation
  // landed would otherwise overwrite state with the pre-mutation list (the new
  // thread vanishes until the next poll): every mutation bumps `mutations`, and
  // a load only applies if neither it nor the page changed while it ran.
  const loadSeq = useRef(0);
  const mutations = useRef(0);

  const load = useCallback(async (quiet = false) => {
    const wanted = pageRef.current;
    const seq = ++loadSeq.current;
    const mutatedAt = mutations.current;
    try {
      const qs = wanted ? `?page=${encodeURIComponent(wanted)}` : '';
      const data = await call<{ threads: ReviewThread[] }>(`/threads${qs}`);
      if (wanted !== pageRef.current) return; // the page changed mid-flight
      if (seq !== loadSeq.current || mutatedAt !== mutations.current) return; // superseded
      setThreads(data.threads);
      setError(null);
    } catch (e) {
      if (!quiet) setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setThreads([]);
    setLoading(true);
    void load();
  }, [page, load]);

  useEffect(() => {
    const tick = () => {
      if (!document.hidden) void load(true);
    };
    const id = window.setInterval(tick, POLL_MS);
    window.addEventListener('focus', tick);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', tick);
    };
  }, [load]);

  const upsert = useCallback((t: ReviewThread) => {
    mutations.current += 1;
    // A response for a page we've since left must not land in this page's list.
    if (pageRef.current && t.page !== pageRef.current) return;
    setThreads((prev) => {
      const i = prev.findIndex((x) => x.id === t.id);
      if (i === -1) return [...prev, t];
      const next = prev.slice();
      next[i] = t;
      return next;
    });
  }, []);

  const create = useCallback(
    async (input: NewThreadInput): Promise<ReviewThread> => {
      const { thread } = await call<{ thread: ReviewThread }>('/threads', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      upsert(thread);
      refreshReviewCount();
      return thread;
    },
    [upsert],
  );

  const reply = useCallback(
    async (id: string, body: string, kind: 'comment' | 'shipped' = 'comment') => {
      const { thread } = await call<{ thread: ReviewThread }>(`/threads/${id}/comments`, {
        method: 'POST',
        body: JSON.stringify({ body, kind }),
      });
      upsert(thread);
      refreshReviewCount(); // a reply can reopen or (shipped) resolve
    },
    [upsert],
  );

  const setStatus = useCallback(
    async (id: string, status: ReviewStatus) => {
      const { thread } = await call<{ thread: ReviewThread }>(`/threads/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      upsert(thread);
      refreshReviewCount();
    },
    [upsert],
  );

  const remove = useCallback(async (id: string) => {
    await call<{ ok: true }>(`/threads/${id}`, { method: 'DELETE' });
    mutations.current += 1;
    setThreads((prev) => prev.filter((t) => t.id !== id));
    refreshReviewCount();
  }, []);

  /** "Send now" — mail the devs this user's unsent comments immediately. */
  const notifyNow = useCallback(
    () => call<{ sent: boolean; comments: number; reason?: string }>('/notify-now', { method: 'POST' }),
    [],
  );

  return { threads, loading, error, reload: load, create, reply, setStatus, remove, notifyNow };
}

/** Open-thread count for the nav badge. Cached per page load; callers
 * refresh it after a mutation with `refreshReviewCount()`. */
let cached: { open: number; at: number } | null = null;
const listeners = new Set<(n: number | null) => void>();

export function refreshReviewCount(): void {
  void call<{ open: number }>('/summary')
    .then((r) => {
      cached = { open: r.open, at: Date.now() };
      for (const fn of listeners) fn(r.open);
    })
    .catch(() => {
      /* a badge is never worth an error */
    });
}

export function useReviewCount(): number | null {
  const [n, setN] = useState<number | null>(cached?.open ?? null);
  useEffect(() => {
    listeners.add(setN);
    if (!cached || Date.now() - cached.at > 60_000) refreshReviewCount();
    return () => {
      listeners.delete(setN);
    };
  }, []);
  return n;
}
